import find from 'lodash/find';
import { v4 as uuidv4 } from 'uuid';

import { TAppAddress, TAppContact, TAppContactId, TAppProvenance } from 'TProtocol/common/models';

export default class ContactHelper {
  static monthNames: string[] = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  static daySuffixes: string[] = ['th', 'st', 'nd', 'rd'];

  static toDateString(contact: TAppContact): string {
    let str = '';
    if (contact.birthday) {
      if (contact.birthday.month && contact.birthday.month.length > 0) {
        str = this.toMonth(contact.birthday.month) + ' ';
      }
      if (contact.birthday.day && contact.birthday.day.length > 0) {
        str += contact.birthday.day;
      }
      if (contact.birthday.year && contact.birthday.year.length > 0) {
        if (str !== '') {
          str += ', ';
        }
        str += contact.birthday.year;
      }
    }
    return str;
  }

  static toBirthdayString(contact: TAppContact): string {
    let str = '';
    const today: Date = this.localDate();
    if (contact.birthday && contact.birthday.month && contact.birthday.month.length > 0
      && contact.birthday.day && contact.birthday.day.length > 0) {
      const day: number = parseInt(contact.birthday.day);
      const month: number = parseInt(contact.birthday.month);
      if (today.getDate() === day && today.getMonth() === (month - 1)) {
        str = 'Today 🎂 Happy Birthday!';
      } else {
        str = this.toMonth(contact.birthday.month) + ' ' + day + ' 🎂';
      }

      let year = 1;
      if (contact.birthday.year && contact.birthday.year.length === 4) {
        year = parseInt(contact.birthday.year);
        const birthdayDate: Date = new Date(year, month - 1, day);
        const age = this.getAge(birthdayDate);
        const suffix = this.ageSuffix(age);
        if (today.getDate() === birthdayDate.getDate() && today.getMonth() === birthdayDate.getMonth()) {
          str = 'Today 🎂 Happy ' + age + suffix + ' Birthday!';
        } else {
          str += ' turning ' + age;
        }
      }
    }
    return str;
  }

  static ageSuffix(age: number): string {
    if (age > 10 && age < 21) {
      return this.daySuffixes[0];
    } else if (age % 10 === 1) {
      return this.daySuffixes[1];
    } else if (age % 10 === 2) {
      return this.daySuffixes[2];
    } else if (age % 10 === 3) {
      return this.daySuffixes[3];
    } else {
      return this.daySuffixes[0];
    }
  }

  static getAge(birthday: Date): number {
    const today: Date = this.localDate();
    const diffYear: number = today.getFullYear() - birthday.getFullYear();
    if (today.getDate() === birthday.getDate() && today.getMonth() === birthday.getMonth()) {
      // Birthday is today, so just return as is.
      return diffYear;
    } else if (today.getMonth() == birthday.getMonth() && today.getDate() > birthday.getDate()) {
      // Birthday has just passed in current month, add a year.
      return diffYear + 1;
    } else if (today.getMonth() > birthday.getMonth()) {
      // Birthday is in previous month, add a year.
      return diffYear + 1;
    } else {
      // Birthday has not occurred yet
      return diffYear;
    }
  }

  static toMonth(num: string): string {
    const numValue: number = this.validMonth(num);
    if (numValue && numValue !== 0) {
      return this.monthNames[numValue - 1];
    } else {
      return '';
    }
  }

  static isValidBirthday(contact: TAppContact): boolean {
    return !!(contact.birthday && contact.birthday.month && contact.birthday.month.length > 0
      && contact.birthday.day && contact.birthday.day.length > 0);
  }

  static validMonth(month: string): number {
    if (!month || isNaN(parseInt(month))) {
      return 0;
    }
    const numValue = +month;
    if (numValue && numValue <= 12 && numValue > 0) {
      return numValue;
    }
    return 0;
  }

  static localDate(): Date {
    return new Date();
  }

  static addressString(address: TAppAddress): string {
    let addStr = '';
    if (address.address1) {
      addStr = address.address1;
    }

    if (address.address2 && address.address1) {
      addStr += ', ' + address.address2;
    } else if (address.address2) {
      addStr += address.address2;
    }

    if ((address.city && address.address1) || (address.city && address.address2)) {
      addStr += ', ' + address.city;
    } else if (address.city) {
      addStr += address.city;
    }

    if (((address.admin1 && address.address1) || (address.admin1 && address.address2)) && (address.city)) {
      addStr += ' ' + address.admin1;
    } else if (((address.admin1 && address.address1) || (address.admin1 && address.address2)) && (!address.city)) {
      addStr += ', ' + address.admin1;
    } else if ((address.admin1 && address.city)) {
      addStr += ' ' + address.admin1;
    }

    if (address.postalCode && !address.city && !address.admin1 && (address.address1 || address.address2)) {
      addStr += ', ' + address.postalCode;
    } else if (address.postalCode) {
      addStr += ' ' + address.postalCode;
    }

    if ((address.country && (address.address1 || address.address2)) && !address.city && !address.admin1 && !address.postalCode) {
      addStr += ', ' + address.country;
    } else if (address.country) {
      addStr += ' ' + address.country;
    }
    return addStr;
  }

  static getName(contact: TAppContact): string {
    if (!contact.name) {
      return '';
    }
    let name = '';
    if (contact.name.firstName && contact.name.firstName.length > 0) {
      name = contact.name.firstName;
    }
    if (contact.name.lastName && contact.name.lastName.length > 0) {
      if (contact.name.firstName && contact.name.firstName.length > 0) {
        name += ' ';
      }
      name += contact.name.lastName;
    }
    if (name === '') {
      name = contact.name.fullName || '';
    }
    return name;
  }

  static incrementClientEditSeqId(contact: TAppContact): void {
    const clientEditSeqIds = find(contact.originalContactIds, { uuid: contact.contactId })?.clientEditSeqIds;
    if (clientEditSeqIds) {
      const existingSeqId = clientEditSeqIds.get(TAppProvenance.MOUSE)
      clientEditSeqIds.set(TAppProvenance.MOUSE, (existingSeqId ?? 0) + 1);
    } else {
      if (!contact.originalContactIds) {
        contact.originalContactIds = [];
      }

      const ids = new Map<TAppProvenance, number>();
      ids.set(TAppProvenance.MOUSE, 1);
      contact.originalContactIds.push(new TAppContactId({
        uuid: uuidv4(),
        clientEditSeqIds: ids
      }));
    }
  }
}
